import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import AccessibilityModal from "./AccessibilityModal";

export default function Footer() {
  const [showAccess, setShowAccess] = useState(false);

  return (
    <footer className="border-t border-[#1E293B] bg-[#07111F]" data-testid="site-footer">
      {/* CTA strip */}
      <div className="border-b border-[#1E293B] bg-[#0B1422]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-7 lg:flex-row lg:items-center lg:px-8">
          <div>
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#38BDF8]">
              Private Walkthrough
            </p>
            <p className="mt-1 font-display text-lg font-bold tracking-[-0.02em] text-[#F8FAFC] sm:text-xl">
              Ready to see the private owner dashboard?
            </p>
          </div>
          <Link
            to="/walkthrough"
            className="group inline-flex items-center gap-2 rounded-md bg-[#22C55E] px-5 py-3 font-body text-sm font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
            data-testid="footer-cta-walkthrough"
          >
            Request Private Walkthrough
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* LEFT — brand */}
          <div className="lg:col-span-5">
            <Logo size={32} />
            <p className="mt-6 max-w-md font-body text-[15px] leading-[1.7] text-[#CBD5E1]">
              InfraYield is building the operating system for modern infrastructure
              ownership intelligence.
            </p>
            <p className="mt-3 max-w-md font-display text-sm font-medium uppercase tracking-[0.14em] text-[#38BDF8]">
              Modern infrastructure ownership starts here.
            </p>
          </div>

          {/* MIDDLE — nav */}
          <div className="lg:col-span-4">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">
              Explore
            </p>
            <ul className="mt-5 space-y-3">
              <FooterLink to="/about" testid="footer-about">About</FooterLink>
              <FooterLink to="/" testid="footer-platform">Platform</FooterLink>
              <FooterLink to="/pilot" testid="footer-pilot">Pilot Access</FooterLink>
              <FooterLink to="/walkthrough" testid="footer-walkthrough">Request Private Walkthrough</FooterLink>
              <FooterLink to="/future-infrastructure-participation" testid="footer-future">Future Infrastructure Participation</FooterLink>
              <FooterLink to="/contact" testid="footer-contact">Contact</FooterLink>
            </ul>
          </div>

          {/* RIGHT — legal */}
          <div className="lg:col-span-3">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">
              Legal
            </p>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/privacy"
                  className="font-body text-sm text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
                  data-testid="footer-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="font-body text-sm text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
                  data-testid="footer-terms"
                >
                  Terms of Use
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setShowAccess(true)}
                  className="inline-flex items-center gap-1 font-body text-sm text-[#CBD5E1] transition-colors hover:text-[#F8FAFC] focus:outline-none focus-visible:text-[#F8FAFC]"
                  data-testid="footer-accessibility"
                >
                  Web Accessibility
                  <ArrowUpRight className="h-3 w-3 opacity-60" />
                </button>
              </li>
              <li>
                <a
                  href="mailto:hello@infrayield.co"
                  className="font-body text-sm text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
                  data-testid="footer-email"
                >
                  hello@infrayield.co
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 border-t border-[#1E293B] pt-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">
              Important Information
            </p>
            <p className="mt-3 max-w-3xl font-body text-[13px] leading-[1.75] text-[#94A3B8]">
              InfraYield is focused on infrastructure visibility, operational
              continuity, and infrastructure organization. Any future
              opportunities, if offered, would only be available through official
              offering materials and compliant legal structures.
            </p>
          </div>

          <div className="flex flex-col items-start gap-4 lg:col-span-4 lg:items-end">
            <p className="font-body text-xs text-[#64748B]">
              © {new Date().getFullYear()} InfraYield. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <AccessibilityModal open={showAccess} onClose={() => setShowAccess(false)} />
    </footer>
  );
}

function FooterLink({ to, children, testid }) {
  return (
    <li>
      <Link
        to={to}
        className="font-body text-sm leading-snug text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
        data-testid={testid}
      >
        {children}
      </Link>
    </li>
  );
}
